/**
 * Cliente mínimo de Server-Sent Events sobre POST.
 *
 * O `EventSource` nativo não serve aqui: só faz GET e não envia o header
 * Authorization. Por isso o parsing do protocolo é feito à mão sobre o
 * ReadableStream devolvido pelo fetch.
 */

/** Sinaliza que a resposta não é um stream SSE — usado para cair no endpoint agregado. */
export class SseUnsupportedError extends Error {
    constructor(contentType) {
        super(`Resposta não é text/event-stream (recebido: ${contentType || 'sem content-type'})`);
        this.name = 'SseUnsupportedError';
    }
}

const parseEventBlock = (block) => {
    let event = 'message';
    const dataLines = [];

    for (const line of block.split('\n')) {
        if (line.startsWith(':')) continue; // comentário (heartbeat)
        if (line.startsWith('event:')) {
            event = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
            dataLines.push(line.slice('data:'.length).trim());
        }
    }

    if (dataLines.length === 0) return null;

    try {
        return { event, data: JSON.parse(dataLines.join('\n')) };
    } catch (error) {
        console.warn('Evento SSE com payload inválido, ignorado:', dataLines.join('\n'));
        return null;
    }
};

/**
 * Faz um POST que responde em SSE e chama `onEvent({ event, data })` a cada evento completo.
 *
 * @throws {Error} quando a resposta não é 2xx (mensagem vinda do backend, quando houver)
 * @throws {SseUnsupportedError} quando a resposta não é text/event-stream
 */
export const postSseStream = async (url, { token, body, signal, onEvent }) => {
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
        },
        body: JSON.stringify(body),
        signal,
    });

    if (!response.ok) {
        let message = `Requisição falhou com status ${response.status}`;
        try {
            const errorBody = await response.json();
            message = errorBody?.message || message;
        } catch {
            // corpo não-JSON: mantém a mensagem padrão
        }
        throw new Error(message);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('text/event-stream')) {
        throw new SseUnsupportedError(contentType);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let separatorIndex;
        while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
            const block = buffer.slice(0, separatorIndex);
            buffer = buffer.slice(separatorIndex + 2);

            const parsed = parseEventBlock(block);
            if (parsed) onEvent(parsed);
        }
    }
    // Um bloco residual no buffer significa evento truncado: o `done` nunca chegou
    // e quem chamou trata o stream como incompleto.
};
